'use client';

import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useCountdown } from '@lib/hooks/useCountdown';
import { differenceInDays } from 'date-fns';
import { interpolateStringWithData } from 'utils';
import { Sale } from './types';

type ProductCardSaleOverlayProps = {
  sale: Sale;
  isActive?: boolean;
};

export const ProductCardSaleOverlay = ({
  sale,
  isActive,
}: ProductCardSaleOverlayProps) => {
  const microcopy = useMicrocopy('product-card', {
    'product-card.sale-ends': 'Sale ends {{time}}',
    'product-card.sale-starts': 'Sale in {{time}}',
    'product-card.countdown-with-days': '{{days}}d {{hours}}h {{minutes}}m',
    'product-card.countdown-hours-only': '{{hours}}h {{minutes}}m',
  });

  const countdownDate = isActive ? sale.dates.ends : sale.dates.starts;
  const countdownDuration = useCountdown(countdownDate);

  if (!countdownDuration) {
    return null;
  }

  // Calculate total days including years and months using differenceInDays for accuracy
  const totalDays = countdownDate
    ? differenceInDays(countdownDate, new Date())
    : 0;
  const hours = countdownDuration.hours ?? 0;
  const minutes = countdownDuration.minutes ?? 0;

  const countdownFormat =
    totalDays > 0
      ? microcopy['product-card.countdown-with-days']
      : microcopy['product-card.countdown-hours-only'];

  const countdownFormatted = interpolateStringWithData(countdownFormat, {
    days: totalDays.toString(),
    hours: hours.toString(),
    minutes: minutes.toString(),
  });

  const microcopyKey = isActive
    ? 'product-card.sale-ends'
    : 'product-card.sale-starts';

  return (
    <div
      className="absolute inset-x-0 bottom-0 flex h-40 flex-col justify-end p-3"
      style={{
        background:
          'linear-gradient(207deg, rgba(208, 0, 0, 0) 60.63%, rgb(0, 0, 0) 99.36%)',
      }}
      data-test="product-card-flash-deal-info"
    >
      <div
        className="inline-flex w-fit items-center justify-center rounded px-1 backdrop-blur-[10px]"
        style={{ backgroundColor: 'rgba(255, 237, 236, 0.15)' }}
        suppressHydrationWarning
        data-test={
          isActive ? 'active-flash-deal-stage' : 'upcoming-flash-deal-stage'
        }
      >
        <span className="text-xs font-normal tracking-tight text-white">
          {interpolateStringWithData(microcopy[microcopyKey], {
            time: countdownFormatted,
          })}
        </span>
      </div>
    </div>
  );
};
